class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 10);

    // Checking a box no longer filters the page immediately — it only
    // updates the Apply button's result count (a lightweight fetch that
    // reads just the product count, touching nothing else visible: not the
    // grid, not the active-filter pills, not the URL). The actual filter
    // only takes effect once Apply is pressed (the 'submit' listener below).
    this.debouncedPreviewCount = debounce((event) => {
      this.previewCount(event);
    }, 250);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedPreviewCount.bind(this));
      facetForm.addEventListener('change', this.debouncedPreviewCount.bind(this));
      facetForm.addEventListener('submit', (event) => {
        event.preventDefault();
        this.onSubmitHandler(event);
        // Close the filter panel on Apply. Without this the panel/overlay
        // stayed open over the freshly-updated grid — the new results were
        // there underneath, but with the panel still covering the screen it
        // read as "nothing happened" / "the filter didn't apply".
        if (typeof FacetFiltersForm.toggleFilterPanel === 'function') {
          FacetFiltersForm.toggleFilterPanel(false);
        }
      });
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.cancelPendingRender();
    const sections = FacetFiltersForm.getSections();
    FacetFiltersForm.toggleCountLoading(true);
    FacetFiltersForm.toggleCollectionLoading(true);

    FacetFiltersForm.abortController = new AbortController();

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event, FacetFiltersForm.abortController.signal);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, signal) {
    fetch(url, { signal })
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      })
      .catch((error) => {
        if (error && error.name === 'AbortError') {
          return;
        }
        console.error('[facets] Failed to render section', error);
        FacetFiltersForm.toggleCollectionLoading(false);
        FacetFiltersForm.toggleCountLoading(false);
      })
      .finally(() => {
        FacetFiltersForm.abortController = null;
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    const container = document.getElementById('ProductGridContainer');
    const parsed = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');
    if (!container || !parsed) return;
    container.innerHTML = parsed.innerHTML;
    FacetFiltersForm.applyViewState(FacetFiltersForm.getPreferredView());
    if (window.HeritageCardGallery && typeof window.HeritageCardGallery.initAll === 'function') {
      window.HeritageCardGallery.initAll();
    }
    if (window.HeritageCardHover && typeof window.HeritageCardHover.initAll === 'function') {
      window.HeritageCardHover.initAll();
    }
    if (window.BabucoFavourites && typeof window.BabucoFavourites.syncAll === 'function') {
      window.BabucoFavourites.syncAll();
    }
    FacetFiltersForm.syncToolbarSort();
  }

  static syncToolbarSort() {
    const params = new URLSearchParams(FacetFiltersForm.searchParamsPrev);
    const sortBy = params.get('sort_by') || 'best-selling';
    const hiddenInput = document.getElementById('ToolbarSortBy');
    if (hiddenInput && hiddenInput.value !== sortBy) {
      hiddenInput.value = sortBy;
    }
    // Also update the custom dropdown UI
    const dropdown = document.querySelector('[data-sort-dropdown]');
    if (!dropdown) return;
    const currentLabel = dropdown.querySelector('[data-sort-current]');
    const options = dropdown.querySelectorAll('[data-sort-value]');
    options.forEach((opt) => {
      const isSelected = opt.dataset.sortValue === sortBy;
      opt.classList.toggle('is-selected', isSelected);
      opt.setAttribute('aria-selected', isSelected);
      if (isSelected && currentLabel) {
        currentLabel.textContent = opt.textContent.trim();
      }
    });
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsed.getElementById('ProductCount');
    if (!sourceCount) {
      FacetFiltersForm.toggleCollectionLoading(false);
      FacetFiltersForm.toggleCountLoading(false);
      return;
    }
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = sourceCount.innerHTML;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = sourceCount.innerHTML;
      containerDesktop.classList.remove('loading');
    }
    FacetFiltersForm.toggleCollectionLoading(false);
    FacetFiltersForm.toggleCountLoading(false);
    FacetFiltersForm.abortController = null;
    // I3: Update Apply button with result count
    FacetFiltersForm.updateApplyButtonCount(parsed);
  }

  static updateApplyButtonCount(parsedDoc) {
    const applyBtn = document.querySelector('.heritage-facets__apply-btn');
    if (!applyBtn) return;
    // Try to read the product count from the fetched page
    const countEl = parsedDoc && parsedDoc.getElementById('ProductCount');
    if (countEl) {
      const countText = countEl.textContent.replace(/[^0-9]/g, '');
      const count = parseInt(countText, 10);
      if (!isNaN(count)) {
        // The span only exists in the server-rendered markup when the page
        // loaded with an active filter already (total_active_values > 0 —
        // see facets.liquid) — on a clean page load with nothing selected
        // yet, it's simply not there. Create it on demand rather than
        // silently no-op'ing, since this is now the only feedback shown
        // before Apply is pressed.
        let countSpan = applyBtn.querySelector('.heritage-facets__apply-count');
        if (!countSpan && count > 0) {
          countSpan = document.createElement('span');
          countSpan.className = 'heritage-facets__apply-count';
          applyBtn.appendChild(countSpan);
        }
        if (countSpan) {
          countSpan.textContent = count > 0 ? `(${count})` : '';
        }
      }
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    FacetFiltersForm.renderHeritageSort(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
    FacetFiltersForm.updateFilterCount();
  }

  static renderHeritageSort(html) {
    // Sort is now in the toolbar as a standalone select (#ToolbarSortBy).
    // syncToolbarSort() keeps it in sync after every render. Nothing to do here.
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const targetElement = document.querySelector(selector);
      const sourceElement = html.querySelector(selector);
      if (!targetElement) return;
      // Re-insert server-rendered active pills so the badge count stays accurate (F2)
      if (sourceElement) {
        targetElement.innerHTML = sourceElement.innerHTML;
      } else {
        targetElement.innerHTML = '';
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
    FacetFiltersForm.updateFilterCount();
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!source || !target) return;
      target.innerHTML = source.innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileForm && mobileForm.closest('menu-drawer')) {
      mobileForm.closest('menu-drawer').bindEvents();
    }
  }

  static initFilterPanel() {
    if (FacetFiltersForm.filterPanelInitialized) return;
    FacetFiltersForm.filterPanelInitialized = true;

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-filter-trigger]');
      if (trigger) {
        event.preventDefault();
        /* Toggle: close if already open, open if closed */
        FacetFiltersForm.toggleFilterPanel(!FacetFiltersForm.filterPanelOpen, trigger);
        return;
      }

      const closeControl = event.target.closest('[data-filter-close]');
      if (closeControl) {
        event.preventDefault();
        FacetFiltersForm.toggleFilterPanel(false);
        return;
      }

      if (event.target.closest('[data-filter-overlay]')) {
        event.preventDefault();
        FacetFiltersForm.toggleFilterPanel(false);
      }
    });

    document.addEventListener('keyup', (event) => {
      if (event.key !== 'Escape') return;
      // Removed isDesktopViewport() guard — ESC should close filters on all viewports
      if (!FacetFiltersForm.filterPanelOpen) return;
      FacetFiltersForm.toggleFilterPanel(false);
    });

    if (window.matchMedia) {
      FacetFiltersForm.filterPanelMedia = window.matchMedia('(min-width: 990px)');
      const handler = FacetFiltersForm.handleViewportChange.bind(FacetFiltersForm);
      if (FacetFiltersForm.filterPanelMedia.addEventListener) {
        FacetFiltersForm.filterPanelMedia.addEventListener('change', handler);
      } else if (FacetFiltersForm.filterPanelMedia.addListener) {
        FacetFiltersForm.filterPanelMedia.addListener(handler);
      }
      FacetFiltersForm.handleViewportChange(FacetFiltersForm.filterPanelMedia);
    } else {
      const panel = document.querySelector('[data-filter-panel]');
      if (panel && !FacetFiltersForm.isDesktopViewport()) {
        panel.setAttribute('aria-hidden', 'true');
      }
    }
  }

  static handleViewportChange(event) {
    const panel = document.querySelector('[data-filter-panel]');
    if (!panel) return;
    const trigger = document.querySelector('[data-filter-trigger]');

    if (event.matches) {
      panel.setAttribute('aria-hidden', 'false');
      FacetFiltersForm.lockScroll(false);
      FacetFiltersForm.filterPanelOpen = false;
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      }
    } else if (!FacetFiltersForm.filterPanelOpen) {
      panel.setAttribute('aria-hidden', 'true');
    }
  }

  static toggleFilterPanel(shouldOpen, sourceControl) {
    const panel = document.querySelector('[data-filter-panel]');
    if (!panel) return;
    const trigger = document.querySelector('[data-filter-trigger]');
    const isDesktop = FacetFiltersForm.isDesktopViewport();

    if (shouldOpen) {
      FacetFiltersForm.filterPanelOpen = true;
      if (sourceControl) {
        FacetFiltersForm.lastFocusedControl = sourceControl;
      }
      panel.setAttribute('aria-hidden', 'false');
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
      }
      // F4: Reset panel scroll to top whenever it is opened
      const scrollBody = panel.querySelector('[data-filter-scroll]') || panel;
      if (scrollBody) scrollBody.scrollTop = 0;
      // F5: Close the sort dropdown if it happens to be open
      const sortDropdown = document.querySelector('[data-sort-dropdown]');
      if (sortDropdown) {
        delete sortDropdown.dataset.open;
        const sortTrigger = sortDropdown.querySelector('.heritage-sort-dropdown__trigger');
        if (sortTrigger) sortTrigger.setAttribute('aria-expanded', 'false');
      }
      if (!isDesktop) {
        FacetFiltersForm.lockScroll(true);
        requestAnimationFrame(() => panel.focus());
        // I9: Activate keyboard focus trap while panel is open on mobile
        FacetFiltersForm.startFocusTrap(panel);
      }
      return;
    }

    FacetFiltersForm.filterPanelOpen = false;
    // I9: Release focus trap
    FacetFiltersForm.stopFocusTrap();
    panel.setAttribute('aria-hidden', isDesktop ? 'false' : 'true');
    if (!isDesktop) {
      FacetFiltersForm.lockScroll(false);
    }
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    }
    const focusTarget = FacetFiltersForm.lastFocusedControl || trigger;
    if (focusTarget) {
      focusTarget.focus();
    }
    FacetFiltersForm.lastFocusedControl = null;
  }

  /* I9: Focus trap helpers */
  static startFocusTrap(panel) {
    FacetFiltersForm._focusTrapHandler = (e) => {
      if (e.key !== 'Tab') return;
      const focusableSelectors = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';
      const focusable = Array.from(panel.querySelectorAll(focusableSelectors)).filter(el => !el.closest('[aria-hidden="true"]'));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', FacetFiltersForm._focusTrapHandler);
  }

  static stopFocusTrap() {
    if (FacetFiltersForm._focusTrapHandler) {
      document.removeEventListener('keydown', FacetFiltersForm._focusTrapHandler);
      FacetFiltersForm._focusTrapHandler = null;
    }
  }

  static lockScroll(shouldLock) {
    const overlay = document.querySelector('[data-filter-overlay]');
    if (shouldLock && FacetFiltersForm.isDesktopViewport()) {
      return;
    }
    document.body.classList.toggle('filter-modal-open', shouldLock);
    if (overlay) {
      overlay.setAttribute('aria-hidden', shouldLock ? 'false' : 'true');
    }
  }

  static isDesktopViewport() {
    if (!window.matchMedia) {
      return window.innerWidth >= 990;
    }
    return window.matchMedia('(min-width: 990px)').matches;
  }

  static updateFilterCount() {
    const badge = document.querySelector('[data-filter-count]');
    if (!badge) return;
    const activeValues = document.querySelectorAll('.heritage-active-facets__pill').length;
    badge.textContent = activeValues > 0 ? activeValues : '';
  }

  static cancelPendingRender() {
    if (FacetFiltersForm.abortController) {
      FacetFiltersForm.abortController.abort();
      FacetFiltersForm.abortController = null;
    }
  }

  static toggleCollectionLoading(isLoading) {
    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) {
      const collection = gridContainer.querySelector('.collection');
      if (collection) {
        collection.classList.toggle('loading', Boolean(isLoading));
        const overlay = collection.querySelector('.loading-overlay');
        if (overlay) {
          overlay.setAttribute('aria-hidden', isLoading ? 'false' : 'true');
        }
      }
    }
    document.body.classList.toggle('collection-loading', Boolean(isLoading));
    FacetFiltersForm.toggleToolbarSpinner(isLoading);
  }

  static toggleToolbarSpinner(isLoading) {
    const spinner = document.querySelector('.heritage-toolbar__count .loading-overlay__spinner');
    if (!spinner) return;
    spinner.classList.toggle('is-active', Boolean(isLoading));
    spinner.setAttribute('aria-hidden', isLoading ? 'false' : 'true');
  }

  static toggleCountLoading(isLoading) {
    const countContainer = document.getElementById('ProductCount');
    const countDesktop = document.getElementById('ProductCountDesktop');
    if (countContainer) {
      countContainer.classList.toggle('loading', Boolean(isLoading));
    }
    if (countDesktop) {
      countDesktop.classList.toggle('loading', Boolean(isLoading));
    }
  }

  static initViewToggle() {
    const toggle = document.querySelector('[data-view-toggle]');
    if (!toggle || toggle.dataset.initialized === 'true') return;
    toggle.dataset.initialized = 'true';
    const buttons = toggle.querySelectorAll('button[data-view]');
    const preferredView = FacetFiltersForm.getPreferredView();
    FacetFiltersForm.applyViewState(preferredView);
    buttons.forEach((button) => {
      const isActive = button.dataset.view === preferredView;
      FacetFiltersForm.setViewToggleState(button, isActive);
      button.addEventListener('click', () => {
        if (button.classList.contains('is-active')) return;
        const view = button.dataset.view;
        FacetFiltersForm.setPreferredView(view);
        FacetFiltersForm.applyViewState(view);
        buttons.forEach((control) => {
          FacetFiltersForm.setViewToggleState(control, control === button);
        });
      });
    });
  }

  static setViewToggleState(button, isActive) {
    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  }

  static applyViewState(view) {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    const resolvedView = view === 'feed' ? 'feed' : 'grid';
    container.classList.remove('is-grid', 'is-feed');
    container.classList.add(resolvedView === 'feed' ? 'is-feed' : 'is-grid');
    container.setAttribute('data-view-state', resolvedView);
    container.style.setProperty('--collection-view-mode', resolvedView);
    const gridElement = container.querySelector('#product-grid');
    if (gridElement) {
      gridElement.setAttribute('data-view', resolvedView);
    }
  }

  static getPreferredView() {
    try {
      return localStorage.getItem(FacetFiltersForm.viewPreferenceKey) || 'grid';
    } catch (error) {
      return 'grid';
    }
  }

  static setPreferredView(view) {
    try {
      const sanitizedView = view === 'feed' ? 'feed' : 'grid';
      localStorage.setItem(FacetFiltersForm.viewPreferenceKey, sanitizedView);
    } catch (error) {
      // noop
    }
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  previewCount(event) {
    const targetEl = event.target;
    const currentForm = targetEl && targetEl.closest ? targetEl.closest('form') : this.querySelector('form');
    if (!currentForm) return;

    const filterParams = this.createSearchParams(currentForm);
    const sortForm = document.getElementById('FacetSortForm');
    const sortParams = sortForm ? this.createSearchParams(sortForm) : '';
    const searchParams = [filterParams, sortParams].filter(Boolean).join('&');

    if (FacetFiltersForm.previewAbortController) FacetFiltersForm.previewAbortController.abort();
    FacetFiltersForm.previewAbortController = new AbortController();

    const sections = FacetFiltersForm.getSections();
    const url = `${window.location.pathname}?section_id=${sections[0].section}&${searchParams}`;

    fetch(url, { signal: FacetFiltersForm.previewAbortController.signal })
      .then((response) => response.text())
      .then((html) => {
        const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
        FacetFiltersForm.updateApplyButtonCount(parsedDoc);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error(error);
        }
      });
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const targetEl = event.target;
    
    // Fallback if target is somehow not inside a form
    const currentForm = targetEl.closest ? targetEl.closest('form') : this.querySelector('form');
    if (!currentForm) return;

    if (targetEl && targetEl.classList && targetEl.classList.contains('mobile-facets__checkbox')) {
      const filterParams = this.createSearchParams(currentForm);
      const sortForm = document.getElementById('FacetSortForm');
      const sortParams = sortForm ? this.createSearchParams(sortForm) : '';
      const combined = [filterParams, sortParams].filter(Boolean).join('&');
      this.onSubmitForm(combined, event);
    } else {
      const forms = [];
      const isMobile = currentForm.id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          const sortForm = document.getElementById('FacetSortForm');
          if (sortForm) forms.push(this.createSearchParams(sortForm));
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.viewPreferenceKey = 'heritageCollectionView';
FacetFiltersForm.filterPanelInitialized = false;
FacetFiltersForm.filterPanelOpen = false;
FacetFiltersForm.lastFocusedControl = null;
FacetFiltersForm.filterPanelMedia = null;
FacetFiltersForm.abortController = null;
FacetFiltersForm.previewAbortController = null;
FacetFiltersForm._focusTrapHandler = null;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.initViewToggle();
FacetFiltersForm.initFilterPanel();
FacetFiltersForm.updateFilterCount();

(function initSortDropdown() {
  const dropdown = document.querySelector('[data-sort-dropdown]');
  if (!dropdown) return;

  const trigger = dropdown.querySelector('.heritage-sort-dropdown__trigger');
  const list = dropdown.querySelector('.heritage-sort-dropdown__list');
  const hiddenInput = document.getElementById('ToolbarSortBy');
  const currentLabel = dropdown.querySelector('[data-sort-current]');

  const open = () => {
    dropdown.dataset.open = '';
    trigger.setAttribute('aria-expanded', 'true');
  };

  const close = () => {
    delete dropdown.dataset.open;
    trigger.setAttribute('aria-expanded', 'false');
  };

  const toggle = () => {
    dropdown.dataset.open !== undefined && dropdown.hasAttribute('data-open') ? close() : open();
  };

  trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    toggle();
  });

  // Select an option
  list.addEventListener('click', (e) => {
    const option = e.target.closest('[data-sort-value]');
    if (!option) return;

    const value = option.dataset.sortValue;

    // Update hidden input value
    if (hiddenInput) {
      hiddenInput.value = value;
    }

    // Update UI
    list.querySelectorAll('[data-sort-value]').forEach((opt) => {
      const sel = opt.dataset.sortValue === value;
      opt.classList.toggle('is-selected', sel);
      opt.setAttribute('aria-selected', sel);
    });

    if (currentLabel) {
      currentLabel.textContent = option.textContent.trim();
    }

    close();

    // Sort should apply the instant an option is picked — unlike the
    // checkbox filters (see the "no longer filters immediately" comment
    // at the top of this file), there's no reason to batch a single sort
    // choice behind a separate Apply press. Dispatching 'input' here used
    // to be enough, back when the shared facet-filters-form listener
    // submitted on 'input' — that listener now only updates the Apply
    // button's preview count, so 'input' alone silently stopped applying
    // the sort at all. requestSubmit() fires the form's own 'submit'
    // listener directly, which still re-renders the grid immediately.
    const sortForm = document.getElementById('FacetSortForm');
    if (sortForm && typeof sortForm.requestSubmit === 'function') {
      sortForm.requestSubmit();
    } else if (hiddenInput) {
      hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
  });

  // Close on outside click
  document.addEventListener('click', (e) => {
    if (!dropdown.contains(e.target)) {
      close();
    }
  });

  // Close on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && dropdown.hasAttribute('data-open')) {
      close();
      trigger.focus();
    }
  });

  // Keyboard navigation within list
  list.addEventListener('keydown', (e) => {
    const options = Array.from(list.querySelectorAll('[data-sort-value]'));
    const focused = list.querySelector('[data-sort-value]:focus');
    const idx = options.indexOf(focused);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = options[idx + 1] || options[0];
      next.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = options[idx - 1] || options[options.length - 1];
      prev.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (focused) focused.click();
    }
  });

  // Make options focusable for keyboard nav
  list.querySelectorAll('[data-sort-value]').forEach((opt) => {
    opt.setAttribute('tabindex', '-1');
  });
})();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

/* ─────────────────────────────────────────────────────────────
   FACET OPTION SEARCH
   Filters visible options in real-time when a filter group has
   more than 10 options and renders a search input.
───────────────────────────────────────────────────────────────*/
(function initFacetSearch() {
  // Wire up a single search container
  function wireSearch(searchEl) {
    if (searchEl._facetSearchWired) return;
    searchEl._facetSearchWired = true;

    const input   = searchEl.querySelector('.facet-search__input');
    const clearBtn = searchEl.querySelector('.facet-search__clear');
    if (!input) return;

    // The sibling list (immediately after the search div)
    const list = searchEl.nextElementSibling;
    if (!list || !list.hasAttribute('data-facet-search-list')) return;

    const items = Array.from(list.children);

    // Build or find the "no results" message node
    let emptyMsg = list.nextElementSibling;
    if (!emptyMsg || !emptyMsg.classList.contains('facet-search-empty')) {
      emptyMsg = document.createElement('p');
      emptyMsg.className = 'facet-search-empty';
      emptyMsg.textContent = 'No options match';
      list.after(emptyMsg);
    }

    function filter(query) {
      const q = query.trim().toLowerCase();
      let visible = 0;

      items.forEach(function(item) {
        const text = (item.textContent || '').toLowerCase();
        const show = !q || text.includes(q);
        item.classList.toggle('facet-search-hidden', !show);
        if (show) visible++;
      });

      const isEmpty = q.length > 0 && visible === 0;
      emptyMsg.classList.toggle('is-visible', isEmpty);

      // Show/hide clear button
      clearBtn && clearBtn.classList.toggle('is-visible', q.length > 0);
    }

    input.addEventListener('input', function(e) {
      e.stopPropagation(); // prevent bubbling to FacetFiltersForm which would trigger AJAX re-render
      filter(this.value);
    });

    // Also stop change events from bubbling (FacetFiltersForm listens to both input + change)
    input.addEventListener('change', function(e) { e.stopPropagation(); });

    if (clearBtn) {
      clearBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        input.value = '';
        filter('');
        input.focus();
      });
    }

    // Reset when filter panel re-renders via AJAX
    // (renderFilters replaces DOM, so new elements are wired on next call)
  }

  // Wire all search containers currently in the DOM
  function wireAll() {
    document.querySelectorAll('[data-facet-search]').forEach(wireSearch);
  }

  // Run on load
  wireAll();

  // Re-wire after AJAX filter renders (FacetFiltersForm replaces .js-filter innerHTML)
  const facetsWrapper = document.getElementById('FacetsWrapperDesktop');
  if (facetsWrapper) {
    new MutationObserver(wireAll).observe(facetsWrapper, { childList: true, subtree: true });
  }
})();
